import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import BarcodeScanField from '@/components/common/BarcodeScanField';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { LocationDto } from '@/types/organization';
import type { MaterialDto } from '@/types/materials';
import type { MaterialUnitDto, MaterialUnitsDto, StockAdjustmentLineInput } from '@/types/adjustments';
import type { BarcodeLookupResult } from '@/types/barcode';

/** Display-only fields live alongside the API shape and are stripped before submit — see
 *  StockAdjustmentsPage's FormLine. */
export type BarcodeFormLine = StockAdjustmentLineInput & {
  batchNo?: string | null;
  clientName?: string | null;
  labelQty?: number | null;
  materialCode?: string | null;
  locationCode?: string | null;
  onHand?: boolean;
};

interface Props {
  warehouseId: string;
  materials: MaterialDto[];
  locations: LocationDto[];
  lines: BarcodeFormLine[];
  setLines: React.Dispatch<React.SetStateAction<BarcodeFormLine[]>>;
  error: string | null;
  setError: (message: string | null) => void;
}

/**
 * Barcode-wise Stock Adjustment: pick a material from the master, see its individual physical units,
 * then tick (or scan) the ones being corrected.
 *
 * The unit list deliberately includes units the system does NOT currently count — a label that was
 * dispatched, or printed but never put away. Those are the "found stock" cases an adjustment exists
 * for, and they are marked so the operator can see they are being brought back on rather than
 * corrected down. A found unit needs a location, because the system has no trustworthy one for it.
 *
 * Scanning is always available and is the faster path at the shelf; the list matters when the label
 * is unreadable or the operator is working from a count sheet.
 */
export default function BarcodeAdjustmentPicker({
  warehouseId, materials, locations, lines, setLines, error, setError,
}: Props) {
  const { t } = useTranslation('stockAdjustment');
  const [materialId, setMaterialId] = useState('');

  const { data: stock, isFetching } = useQuery({
    queryKey: ['material-units', materialId, warehouseId],
    queryFn: async () => (await apiClient.get<MaterialUnitsDto>('/stock-adjustments/material-units', {
      params: { materialId, warehouseId },
    })).data,
    enabled: Boolean(materialId && warehouseId),
  });

  const units = stock?.units ?? [];

  const selectedByBarcode = useMemo(
    () => new Map(lines.filter((l) => l.scannedBarcode).map((l) => [l.scannedBarcode!.toLowerCase(), l])),
    [lines],
  );

  const addUnit = (unit: MaterialUnitDto, resolvedMaterialId: string) => {
    setLines((prev) => [...prev.filter((l) => l.scannedBarcode?.toLowerCase() !== unit.barcode.toLowerCase()), {
      materialId: resolvedMaterialId,
      locationId: unit.locationId,
      batchId: unit.batchId,
      batchNo: unit.batchNo,
      // An on-hand unit starts at what the label says is there, so leaving it alone is a no-op and
      // lowering it writes the difference off. A found unit starts at its full quantity too — the
      // operator is putting the whole unit back on the books.
      adjustedQty: unit.qty,
      remarks: null,
      scannedBarcode: unit.barcode,
      labelQty: unit.qty,
      locationCode: unit.locationCode,
      onHand: unit.onHand,
    }]);
  };

  const removeBarcode = (barcode: string) => {
    setLines((prev) => prev.filter((l) => l.scannedBarcode?.toLowerCase() !== barcode.toLowerCase()));
  };

  const toggleUnit = (unit: MaterialUnitDto) => {
    setError(null);
    if (selectedByBarcode.has(unit.barcode.toLowerCase())) {
      removeBarcode(unit.barcode);
    } else {
      addUnit(unit, materialId);
    }
  };

  const handleScan = (result: BarcodeLookupResult, barcode: string) => {
    if (!result.labelSource) {
      setError(t('form.scanNeedsLabel'));
      return;
    }
    if (selectedByBarcode.has(barcode.toLowerCase())) {
      setError(t('form.scanDuplicate'));
      return;
    }
    setError(null);

    // A scan can bring in a unit that isn't in the list at all — a different material's, or one the
    // list would not have shown. The lookup already knows everything the line needs.
    const onHand = result.labelStatus === 'InStock' && Boolean(result.labelLocationId);
    addUnit({
      barcode,
      source: (result.labelSource ?? 'Grn') as MaterialUnitDto['source'],
      qty: result.labelQty ?? 0,
      locationId: result.labelLocationId ?? null,
      locationCode: result.labelLocationCode ?? null,
      batchId: result.batchId,
      batchNo: result.batchNo,
      labelStatus: result.labelStatus ?? 'InStock',
      stockStatus: 'Available',
      onHand,
    }, result.materialId!);
  };

  const patch = (barcode: string, changes: Partial<BarcodeFormLine>) => {
    setLines((prev) => prev.map((l) => (l.scannedBarcode?.toLowerCase() === barcode.toLowerCase() ? { ...l, ...changes } : l)));
  };

  const locationOptions = locations.map((loc) => ({ value: loc.id, label: loc.code }));

  // Only genuinely scanned units count as selected — `lines` carries a blank placeholder row that
  // belongs to the row-wise mode and must never show up here.
  const selectedLines = lines.filter((l) => l.scannedBarcode);
  const elsewhere = selectedLines.filter(
    (l) => !units.some((u) => u.barcode.toLowerCase() === l.scannedBarcode!.toLowerCase()),
  );

  return (
    <Stack spacing={2}>
      <Alert severity={error ? 'error' : 'info'} onClose={error ? () => setError(null) : undefined}>
        {error ?? t('form.barcodePickerHint')}
      </Alert>

      <SearchableSelect
        label={t('form.material')}
        value={materialId}
        onChange={setMaterialId}
        fullWidth
        options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
      />

      <BarcodeScanField warehouseId={warehouseId || null} onResolved={handleScan} />

      {materialId ? (
        <Box>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2">{t('form.unitsTitle')}</Typography>
            {stock ? (
              <Typography variant="body2" color="text.secondary">
                {t('form.stockSummary', {
                  onHand: stock.quantityOnHand,
                  available: stock.quantityAvailable,
                  allocated: stock.quantityAllocated,
                })}
              </Typography>
            ) : undefined}
          </Stack>

          {/* The list is capped at the stock figure, so it can never offer units that are not there.
              When labels had to be held back to achieve that, say so — silently showing fewer units
              than the system has labels for would look like data going missing. */}
          {stock && stock.hiddenUnitCount > 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t('form.unitsHidden', { count: stock.hiddenUnitCount, onHand: stock.quantityOnHand })}
            </Alert>
          ) : undefined}

          {isFetching ? <LinearProgress /> : undefined}

          {!isFetching && units.length === 0 ? (
            <Alert severity="warning">{t('form.noUnits')}</Alert>
          ) : undefined}

          {units.length > 0 ? (
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
              {units.map((u) => {
                const line = selectedByBarcode.get(u.barcode.toLowerCase());
                return (
                  <Box key={u.barcode} sx={{ py: 0.5 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Checkbox size="small" checked={Boolean(line)} onChange={() => toggleUnit(u)} sx={{ mt: 0.5 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">{u.barcode}</Typography>
                          {!u.onHand ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={u.labelStatus === 'Dispatched' ? t('form.unitDispatched')
                                : u.labelStatus === 'Consumed' ? t('form.unitConsumed')
                                : t('form.unitNotPutAway')}
                            />
                          ) : undefined}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {t('form.unitMeta', {
                            qty: u.qty,
                            source: u.source,
                            batch: u.batchNo ?? '—',
                            location: u.locationCode ?? t('form.none'),
                          })}
                        </Typography>

                        {line ? (
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <TextField
                              type="number"
                              size="small"
                              label={t('form.foundQty')}
                              value={line.adjustedQty}
                              onChange={(e) => patch(u.barcode, { adjustedQty: Number(e.target.value) })}
                              sx={{ width: 130 }}
                            />
                            {/* A found unit has no location the system can trust, so the operator
                                states where it actually turned up. */}
                            {!u.onHand ? (
                              <SearchableSelect
                                label={t('form.foundAt')}
                                value={line.locationId ?? ''}
                                onChange={(v) => patch(u.barcode, { locationId: v || null })}
                                size="small"
                                options={locationOptions}
                              />
                            ) : undefined}
                            <TextField
                              size="small"
                              label={t('form.remarks')}
                              value={line.remarks ?? ''}
                              onChange={(e) => patch(u.barcode, { remarks: e.target.value || null })}
                              sx={{ flexGrow: 1 }}
                            />
                          </Stack>
                        ) : undefined}
                      </Box>
                    </Stack>
                    <Divider sx={{ mt: 0.5 }} />
                  </Box>
                );
              })}
            </Box>
          ) : undefined}
        </Box>
      ) : undefined}

      {/* Units added by scanning a barcode outside the current material's list still need to be
          visible and removable, or they would be submitted invisibly. Only real scans qualify — the
          page seeds `lines` with one blank placeholder for the row-wise mode, and without the
          scannedBarcode guard that placeholder would render here as a phantom selected unit. */}
      {elsewhere.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('form.scannedElsewhere')}</Typography>
          {elsewhere
            .map((l) => (
              <Stack key={l.scannedBarcode} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                <Checkbox size="small" checked onChange={() => removeBarcode(l.scannedBarcode!)} />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {l.materialCode ?? ''} {l.scannedBarcode}
                </Typography>
                <TextField
                  type="number" size="small" label={t('form.foundQty')} value={l.adjustedQty}
                  onChange={(e) => patch(l.scannedBarcode!, { adjustedQty: Number(e.target.value) })}
                  sx={{ width: 120 }}
                />
                {!l.onHand ? (
                  <SearchableSelect
                    label={t('form.foundAt')} value={l.locationId ?? ''} size="small"
                    onChange={(v) => patch(l.scannedBarcode!, { locationId: v || null })}
                    options={locationOptions}
                  />
                ) : undefined}
              </Stack>
            ))}
        </Box>
      ) : undefined}

      <Typography variant="body2" color="text.secondary">
        {t('form.selectedCount', { count: selectedLines.length })}
      </Typography>
    </Stack>
  );
}
